import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, resolveImage, formatApiError } from '@/lib/api';
import { ArrowLeft, Calendar, ArrowRight } from 'lucide-react';
import SEO from '@/components/dubinko/SEO';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [slug]);

  const schema = post ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image ? [resolveImage(post.cover_image)] : undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { '@type': 'Organization', name: 'Dubinko' },
    publisher: { '@type': 'Organization', name: 'Dubinko' },
  } : null;

  return (
    <main data-testid="blog-post-page">
      <SEO
        title={post ? post.title : 'Savet'}
        description={post?.excerpt || 'Saveti za čišćenje i održavanje doma — Dubinko Niš.'}
        path={`/saveti/${slug}`}
        type="article"
        image={post?.cover_image ? resolveImage(post.cover_image) : undefined}
        schema={schema}
      />
      <section className="section-pad">
        <div className="container-x max-w-3xl">
          <Link to="/saveti" data-testid="blog-back"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> Nazad na savete
          </Link>

          {loading ? (
            <div className="text-slate-500 mt-8">Učitavanje…</div>
          ) : error ? (
            <div className="mt-8 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>
          ) : post ? (
            <article className="mt-6">
              <h1 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">{post.title}</h1>
              <div className="mt-4 text-sm text-slate-500 inline-flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(post.created_at).toLocaleDateString('sr-RS', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              {post.cover_image && (
                <img src={resolveImage(post.cover_image)} alt={post.title} loading="lazy" decoding="async"
                  className="mt-8 rounded-3xl w-full object-cover aspect-[16/9] shadow-lg" />
              )}
              <div className="prose prose-slate max-w-none mt-8 text-slate-700 leading-relaxed whitespace-pre-line text-lg">
                {post.content}
              </div>

              <div className="mt-12 p-8 rounded-3xl text-white text-center"
                style={{ background: 'linear-gradient(135deg, #023E8A 0%, #5B21B6 100%)' }}>
                <h2 className="font-display text-2xl font-bold">Treba vam profesionalno čišćenje?</h2>
                <p className="mt-2 text-blue-100/90">Zakažite termin online u manje od 2 minuta.</p>
                <Link to="/rezervacija"
                  className="inline-flex items-center gap-2 mt-5 bg-white text-slate-900 px-6 py-3 rounded-full font-semibold">
                  Zakaži čišćenje <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ) : null}
        </div>
      </section>
    </main>
  );
}
